import React, { useEffect, useState } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import logoImage from "../../assets/logo-prima-hitam.png";
import formatRupiah from "utils/formatRupiah";
import moment from "moment";
import "moment/locale/id";

moment.locale("id");

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontSize: 9,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },

  /* ================= HEADER ================= */
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
    paddingBottom: 6,
    borderBottom: "1px solid #dcdcdc",
  },

  headerLeft: {
    width: "55%",
  },

  headerRight: {
    width: "45%",
    alignItems: "flex-end",
  },

  logo: {
    width: 90,
    marginTop: 4,
  },

  documentTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 6,
    letterSpacing: 1,
  },

  /* ================= EMPLOYEE INFO ================= */

  employeeInfoRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
  },

  employeeInfoLabel: {
    fontSize: 8,
    color: "#666",
  },

  employeeInfoValue: {
    fontSize: 9,
    fontWeight: "bold",
  },

  /* ================= MAIN CONTENT ================= */

  mainContentColumns: {
    flexDirection: "row",
    marginTop: 8,
    paddingTop: 6,
    borderTop: "1px solid #eee",
  },

  financialColumn: {
    width: "50%",
    paddingRight: 10,
  },

  columnTitle: {
    fontSize: 10,
    fontWeight: "bold",
    marginBottom: 6,
    paddingBottom: 3,
    borderBottom: "1px solid #e5e5e5",
  },

  financialRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  financialLabel: {
    fontSize: 9,
  },

  financialValue: {
    fontSize: 9,
    textAlign: "right",
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 6,
    paddingTop: 5,
    borderTop: "1px solid #ccc",
    fontWeight: "bold",
  },

  /* ================= NET PAY ================= */

  netPayRow: {
    marginTop: 12,
    padding: 10,
    borderColor: "#111",
    borderWidth: 1,
    borderRadius: 6,
    flexDirection: "row",
    justifyContent: "space-between",
  },

  netPayText: {
    fontSize: 11,
    fontWeight: "bold",
    color: "#fff",
  },

  /* ================= FOOTER ================= */

  dateText: {
    marginTop: 12,
    textAlign: "right",
    fontSize: 8,
    color: "#666",
  },

  signatureContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },

  signatureBox: {
    width: "30%",
    alignItems: "center",
  },

  signatureLine: {
    width: "80%",
    height: 1,
    backgroundColor: "#000",
    marginTop: 35,
    marginBottom: 4,
  },

  signatureLabel: {
    fontSize: 9,
    fontWeight: "bold",
  },

  signatureName: {
    fontSize: 8,
  },
});

const PayslipDocument = ({ data }) => {
  // Calculate totals
  const totalPendapatan =
    (parseFloat(data?.gaji_pokok) || 0) +
    (parseFloat(data?.lembur) || 0) +
    (parseFloat(data?.rajin) || 0) +
    (parseFloat(data?.transportasi) || 0) +
    (parseFloat(data?.pulsa) || 0) +
    (parseFloat(data?.tunjangan_jabatan) || 0) +
    (parseFloat(data?.uang_makan) || 0);

  const totalPotongan =
    (parseFloat(data?.potongan_nbh) || 0) +
    (parseFloat(data?.potongan_tabungan) || 0) +
    (parseFloat(data?.potongan_pinjaman) || 0);

  return (
    <Document>
      <Page size="A5" orientation="landscape" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          {/* Header Kiri - Informasi Karyawan */}
          <View style={styles.headerLeft}>
            <View style={styles.employeeInfoRow}>
              <Text style={styles.employeeInfoLabel}>{data?.id_karyawan}</Text>
              <Text style={[styles.employeeInfoValue, { fontWeight: "bold" }]}>
                {data?.nama_karyawan}
              </Text>
            </View>
            <View style={styles.employeeInfoRow}>
              <Text style={[styles.employeeInfoValue, { textAlign: "left" }]}>
                {data?.nama_jabatan?.split(" - ")[1] ?? "-"}
              </Text>
              <Text style={styles.employeeInfoValue}>
                {data?.nama_jabatan?.split(" - ")[0] ?? "-"}
              </Text>
            </View>
            <View style={styles.employeeInfoRow}>
              <Text style={[styles.employeeInfoValue, { textAlign: "left" }]}>
                Hari Kerja
              </Text>
              <Text style={styles.employeeInfoValue}>
                {data?.jumlah_hadir + " Hari" ?? "-"}
              </Text>
            </View>
            <View style={styles.employeeInfoRow}>
              <Text style={[styles.employeeInfoValue, { textAlign: "left" }]}>
                Hari Izin
              </Text>
              <Text style={styles.employeeInfoValue}>
                {data?.jumlah_izin + " Hari" ?? "-"}
              </Text>
            </View>
            <View style={styles.employeeInfoRow}>
              <Text style={[styles.employeeInfoValue, { textAlign: "left" }]}>
                Total Telat
              </Text>
              <Text style={styles.employeeInfoValue}>
                {data?.total_telat + " Menit" ?? "-"}
              </Text>
            </View>
            <View style={[styles.employeeInfoRow, { marginBottom: 0 }]}>
              <Text style={[styles.employeeInfoValue, { textAlign: "left" }]}>
                Masa Kerja
              </Text>
              <Text style={styles.employeeInfoValue}>
                {data?.lama_kerja_hari + " Hari" ?? "-"}
              </Text>
            </View>
          </View>

          {/* Header Kanan - Logo dan Judul */}
          <View style={styles.headerRight}>
            <Text style={styles.documentTitle}>
              SLIP GAJI -{" "}
              {data?.bulan &&
                moment()
                  .month(parseInt(data?.bulan) - 1)
                  .year(data?.tahun)
                  .format("MMMM YYYY")}
            </Text>
            <Image
              src={logoImage}
              alt="PRIMA centre Logo"
              style={styles.logo}
            />
          </View>
        </View>

        {/* Main Financial Content - Two Columns */}
        <View style={styles.mainContentColumns}>
          {/* Left Column: Pendapatan */}
          <View style={styles.financialColumn}>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Gaji Pokok</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.gaji_pokok)) ?? "-"}
              </Text>
            </View>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Rajin</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.rajin)) ?? "-"}
              </Text>
            </View>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Transportasi</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.transportasi)) ?? "-"}
              </Text>
            </View>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Pulsa</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.pulsa)) ?? "-"}
              </Text>
            </View>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Tunjangan Jabatan</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.tunjangan_jabatan)) ?? "-"}
              </Text>
            </View>
            <View
              style={[
                styles.financialRow,
                parseFloat(data?.lembur) == 0 && { marginBottom: 0 },
              ]}
            >
              <Text style={styles.financialLabel}>Uang Makan</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.uang_makan)) ?? "-"}
              </Text>
            </View>
            {parseFloat(data?.lembur) > 0 && (
              <View style={[styles.financialRow, { marginBottom: 0 }]}>
                <Text style={styles.financialLabel}>Lembur</Text>
                <Text style={styles.financialValue}>
                  {formatRupiah(parseFloat(data?.lembur)) ?? "-"}
                </Text>
              </View>
            )}
            <View style={styles.totalRow}>
              <Text style={[styles.financialLabel, { fontWeight: "bold" }]}>
                Total Salary
              </Text>
              <Text style={[styles.financialValue, { fontWeight: "bold" }]}>
                {formatRupiah(totalPendapatan)}
              </Text>
            </View>
          </View>

          {/* Right Column: Potongan & NBH */}
          <View style={[styles.financialColumn, { paddingLeft: 8 }]}>
            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Potongan NBH</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.potongan_nbh)) ?? "-"}
              </Text>
            </View>
            <View style={[styles.financialRow, { marginBottom: "2px" }]}>
              <Text
                style={[styles.financialLabel, { fontSize: 8, color: "gray" }]}
              >
                Sisa NBH bulan lalu
              </Text>
              <Text
                style={[styles.financialValue, { fontSize: 8, color: "gray" }]}
              >
                {formatRupiah(parseFloat(data?.sisa_nbh_bln_ini)) ?? "-"}
              </Text>
            </View>
            <View style={[styles.financialRow, { marginBottom: "2px" }]}>
              <Text
                style={[styles.financialLabel, { fontSize: 8, color: "gray" }]}
              >
                NBH bulan ini
              </Text>
              <Text
                style={[styles.financialValue, { fontSize: 8, color: "gray" }]}
              >
                {formatRupiah(parseFloat(data?.nbh_bln_ini)) ?? "-"}
              </Text>
            </View>
            <View style={styles.financialRow}>
              <Text
                style={[styles.financialLabel, { fontSize: 8, color: "gray" }]}
              >
                Sisa NBH
              </Text>
              <Text
                style={[styles.financialValue, { fontSize: 8, color: "gray" }]}
              >
                {formatRupiah(parseFloat(data?.sisa_nbh)) ?? "-"}
              </Text>
            </View>
            <View style={[styles.financialRow, { marginBottom: "2px" }]}>
              <Text style={styles.financialLabel}>Potongan Tabungan</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.potongan_tabungan)) ?? "-"}
              </Text>
            </View>
            <View style={[styles.financialRow, { marginBottom: "4px" }]}>
              <Text
                style={[styles.financialLabel, { fontSize: 8, color: "gray" }]}
              >
                Total Tabungan
              </Text>
              <Text
                style={[styles.financialValue, { fontSize: 8, color: "gray" }]}
              >
                {formatRupiah(parseFloat(data?.total_tabungan)) ?? "-"}
              </Text>
            </View>

            <View style={styles.financialRow}>
              <Text style={styles.financialLabel}>Potongan Pinjaman</Text>
              <Text style={styles.financialValue}>
                {formatRupiah(parseFloat(data?.potongan_pinjaman))}
              </Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={[styles.financialLabel, { fontWeight: "bold" }]}>
                Total Potongan
              </Text>
              <Text style={[styles.financialValue, { fontWeight: "bold" }]}>
                {formatRupiah(totalPotongan)}
              </Text>
            </View>
          </View>
        </View>

        {/* Net Pay Section */}
        <View style={styles.netPayRow}>
          <Text>Total Salary Di Terima</Text>
          <Text>{formatRupiah(parseFloat(data?.total_gaji)) ?? "-"}</Text>
        </View>

        {/* Signature Section */}
        <View style={styles.dateText}>
          <Text>Sukabumi, {moment().format("D MMMM YYYY")}</Text>
        </View>
        <View style={styles.signatureContainer}>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Dibuat Oleh</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>
              ...................................
            </Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Diperiksa Oleh</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>
              ...................................
            </Text>
          </View>
          <View style={styles.signatureBox}>
            <Text style={styles.signatureLabel}>Diterima Oleh</Text>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>
              {data?.nama_karyawan?.split(" ")[0] ?? "Moch"}
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

const CetakSlip = () => {
  const [dataSlip, setDataSlip] = useState(null);

  useEffect(() => {
    const data_slip_source = sessionStorage.getItem("dataslipgaji");
    if (data_slip_source) {
      setDataSlip(JSON.parse(data_slip_source));
    }
  }, []);

  return (
    <div style={{ height: "100vh" }}>
      <PDFViewer width="100%" height="100%">
        <PayslipDocument data={dataSlip} />
      </PDFViewer>
    </div>
  );
};

export default CetakSlip;
